import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPrograms,
  deleteProgram,
  listProgramPlaces,
  listPlaces,
  type Program,
} from '@littlebooker/dal';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { ProgramFormDialog } from '@/components/ProgramFormDialog';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash2 } from 'lucide-react';

export default function ProgramsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = !user || user.role === 'owner' || user.role === 'admin';

  const [showCreate, setShowCreate] = useState(false);
  const [deletingProgram, setDeletingProgram] = useState<Program | null>(null);

  const { data: programs = [], isLoading } = useQuery({
    queryKey: ['programs'],
    queryFn: () => listPrograms(),
  });

  const { data: stages = [] } = useQuery({
    queryKey: ['places', { type: 'stage' }],
    queryFn: () => listPlaces({ type: 'stage' }),
  });

  const { data: stageCounts = {} } = useQuery({
    queryKey: ['program-place-counts', programs.map((p) => p.id).sort().join(',')],
    enabled: programs.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        programs.map(async (p) => {
          const links = await listProgramPlaces(p.id);
          return [p.id, links.length] as const;
        })
      );
      return Object.fromEntries(entries) as Record<string, number>;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProgram(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      setDeletingProgram(null);
    },
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Programs</h1>
          <p className="text-muted-foreground">
            Multi-stage groupings of performances (festivals, club weekends, residencies)
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New program
        </Button>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : programs.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No programs yet</CardTitle>
            <CardDescription>
              Create a program to group multiple performances across stages into a single timetable.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Starts</TableHead>
                <TableHead>Ends</TableHead>
                <TableHead>Stages</TableHead>
                {canDelete && <TableHead className="w-12">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {programs.map((program) => (
                <TableRow
                  key={program.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/programs/${program.id}`)}
                >
                  <TableCell className="font-medium">{program.name}</TableCell>
                  <TableCell className="text-muted-foreground">{program.slug}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(program.start_time).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(program.end_time).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {stageCounts[program.id] ?? '—'}
                  </TableCell>
                  {canDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingProgram(program)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {showCreate && (
        <ProgramFormDialog
          mode="create"
          program={null}
          stages={stages}
          onClose={() => setShowCreate(false)}
        />
      )}

      {deletingProgram && (
        <DeleteConfirmationDialog
          open={!!deletingProgram}
          onOpenChange={(open) => !open && setDeletingProgram(null)}
          onConfirm={() => deleteMutation.mutate(deletingProgram.id)}
          entityName={deletingProgram.name}
          entityType="program"
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
